import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, User, Menu, Search, Star, Clock, MapPin, Heart, Share2, Truck, Award, ShieldCheck, Filter, TrendingUp, ChevronRight } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { formatPrice } from '../../lib/utils';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const HomePage = () => {
  const { getCartItemCount, setIsCartOpen } = useCart();
  const { user } = useAuth();
  const [foodItems, setFoodItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use hardcoded data to test layout while fixing Firestore issue
    const hardcodedFoodItems = [
      {
        id: '1',
        name: 'Chicken Biryani',
        description: 'Aromatic basmati rice cooked with tender chicken pieces and traditional spices.',
        category: 'main-course',
        price: { half: 180, full: 320 },
        imageUrl: '/images/placeholder-main-course.svg',
        rating: 4.5,
        prepTime: '25-30 mins',
        isPopular: true,
        isVeg: false
      },
      {
        id: '2',
        name: 'Chicken Pakora',
        description: 'Crispy fried chicken fritters, marinated in spices and gram flour batter.',
        category: 'appetizers',
        price: { half: 120, full: 200 },
        imageUrl: '/images/placeholder-appetizers.svg',
        rating: 4.1,
        prepTime: '15-20 mins',
        isPopular: false,
        isVeg: false
      },
      {
        id: '3',
        name: 'Chicken Tikka Masala',
        description: 'Grilled chicken pieces cooked in a rich, creamy, and spicy tomato gravy.',
        category: 'main-course',
        price: { half: 200, full: 350 },
        imageUrl: '/images/placeholder-main-course.svg',
        rating: 4.6,
        prepTime: '30-35 mins',
        isPopular: true,
        isVeg: false
      },
      {
        id: '4',
        name: 'Gulab Jamun',
        description: 'Soft and spongy milk dumplings soaked in sweet rose-flavored syrup.',
        category: 'desserts',
        price: { half: 80, full: 140 },
        imageUrl: '/images/placeholder-desserts.svg',
        rating: 4.3,
        prepTime: '10-15 mins',
        isPopular: false,
        isVeg: true
      },
      {
        id: '5',
        name: 'Mango Lassi',
        description: 'Refreshing yogurt drink blended with sweet mango pulp.',
        category: 'beverages',
        price: { half: 60, full: 100 },
        imageUrl: '/images/placeholder-beverages.svg',
        rating: 4.4,
        prepTime: '5-10 mins',
        isPopular: false,
        isVeg: true
      },
      {
        id: '6',
        name: 'Masala Chai',
        description: 'Traditional Indian spiced tea with aromatic spices.',
        category: 'beverages',
        price: { half: 30, full: 50 },
        imageUrl: '/images/placeholder-beverages.svg',
        rating: 4.2,
        prepTime: '5-8 mins',
        isPopular: false,
        isVeg: true
      },
      {
        id: '7',
        name: 'Mutton Biryani',
        description: 'Fragrant basmati rice cooked with tender mutton pieces and aromatic spices.',
        category: 'main-course',
        price: { half: 220, full: 380 },
        imageUrl: '/images/placeholder-main-course.svg',
        rating: 4.7,
        prepTime: '35-40 mins',
        isPopular: true,
        isVeg: false
      },
      {
        id: '8',
        name: 'Paneer Butter Masala',
        description: 'Soft paneer cubes cooked in rich, creamy tomato and butter gravy.',
        category: 'main-course',
        price: { half: 160, full: 280 },
        imageUrl: '/images/placeholder-main-course.svg',
        rating: 4.4,
        prepTime: '20-25 mins',
        isPopular: false,
        isVeg: true
      },
      {
        id: '9',
        name: 'Ras Malai',
        description: 'Soft cottage cheese dumplings in sweetened milk with cardamom.',
        category: 'desserts',
        price: { half: 90, full: 160 },
        imageUrl: '/images/placeholder-desserts.svg',
        rating: 4.5,
        prepTime: '10-15 mins',
        isPopular: false,
        isVeg: true
      },
      {
        id: '10',
        name: 'Samosa',
        description: 'Crispy fried pastry filled with spiced potato and peas mixture.',
        category: 'appetizers',
        price: { half: 40, full: 70 },
        imageUrl: '/images/placeholder-appetizers.svg',
        rating: 4.0,
        prepTime: '15-20 mins',
        isPopular: false,
        isVeg: true
      }
    ];

    const hardcodedCategories = [
      { id: 'appetizers', name: 'Appetizers' },
      { id: 'beverages', name: 'Beverages' },
      { id: 'desserts', name: 'Desserts' },
      { id: 'main-course', name: 'Main Course' }
    ];

    setFoodItems(hardcodedFoodItems);
    setCategories(hardcodedCategories);
    setLoading(false);
  }, []);

  const fetchFoodItems = async () => {
    try {
      const q = query(collection(db, 'food_items'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFoodItems(items);
      console.log('Fetched food items:', items); // Debug log
    } catch (error) {
      console.error('Error fetching food items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const cats = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(cats);
      console.log('Fetched categories:', cats); // Debug log
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filteredItems = foodItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getPlaceholderImage = (category) => {
    const categoryMap = {
      'main-course': '/images/placeholder-main-course.svg',
      'desserts': '/images/placeholder-desserts.svg',
      'beverages': '/images/placeholder-beverages.svg',
      'appetizers': '/images/placeholder-appetizers.svg'
    };
    return categoryMap[category] || '/placeholder.svg';
  };

  const FoodItemCard = ({ item }) => {
    const { addToCart } = useCart();
    const [selectedPortion, setSelectedPortion] = useState('half');
    const [isLiked, setIsLiked] = useState(false);

    const handleAddToCart = () => {
      addToCart(item, selectedPortion);
    };

    return (
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          border: '2px solid #f97316',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '300px',
          margin: '0 auto',
          position: 'relative'
        }}
      >
        <div className="relative">
          <img
            src={item.imageUrl || getPlaceholderImage(item.category)}
            alt={item.name}
            style={{
              width: '100%',
              height: '224px',
              objectFit: 'cover',
              borderRadius: '16px 16px 0 0'
            }}
            onError={(e) => {
              e.target.src = getPlaceholderImage(item.category);
            }}
          />
          
          {/* Overlay Badges */}
          <div className="absolute top-3 left-3 flex flex-col space-y-2">
            {item.isPopular && (
              <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                🔥 Popular
              </span>
            )}
            {item.isVeg !== false && (
              <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                🥗 Veg
              </span>
            )}
          </div>

          {/* Like Button */}
          <button
            onClick={() => setIsLiked(!isLiked)}
            className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'text-red-500 fill-current' : 'text-gray-600'}`} />
          </button>

          {/* Share Button */}
          <button className="absolute top-12 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors">
            <Share2 className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        <div className="p-5">
          <div className="space-y-4">
            {/* Title and Description */}
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">{item.name}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
            </div>

            {/* Rating and Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center bg-green-100 px-3 py-1 rounded-full">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-semibold text-gray-800 ml-1">{item.rating || 4.5}</span>
                </div>
                <div className="flex items-center text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  <span className="text-sm">{item.prepTime || '25-30 mins'}</span>
                </div>
              </div>
            </div>

            {/* Price Options */}
            <div className="space-y-3">
              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedPortion('half')}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
                    selectedPortion === 'half'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-sm opacity-90">Half Portion</div>
                    <div className="text-lg font-bold">{formatPrice(item.price?.half || 0)}</div>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedPortion('full')}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
                    selectedPortion === 'full'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-sm opacity-90">Full Portion</div>
                    <div className="text-lg font-bold">{formatPrice(item.price?.full || 0)}</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-24 w-24 border-4 border-orange-200 border-t-orange-500 mx-auto mb-6"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full animate-pulse"></div>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Loading delicious food...</h3>
          <p className="text-gray-600">Preparing your favorite meals</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f8f9fa',
        overflowY: 'auto',
        width: '100%',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {/* Desktop-Friendly Header */}
      <header style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: '1px solid #fed7aa'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '16px 24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Logo Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-xl">B</span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h1 className="font-black text-xl text-gray-900">B.Tech Bawarchi</h1>
                <div className="flex items-center space-x-1">
                  <MapPin className="w-3 h-3 text-orange-500" />
                  <p className="text-xs text-gray-600 font-medium">Delivering happiness</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ShoppingCart className="w-6 h-6" />
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg animate-bounce">
                    {getCartItemCount()}
                  </span>
                )}
              </button>
              
              <button className="p-3 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition-all duration-200 shadow-lg hover:shadow-xl">
                <User className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
        <div className="absolute inset-0 bg-black/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-16 text-center text-white">
          <div>
            <h2 className="text-3xl font-black mb-4 leading-tight">
              🍽️ Delicious Food,<br />
              <span className="text-yellow-300">Delivered Fast!</span>
            </h2>
            <p className="text-lg opacity-90 mb-8 font-medium">
              Order fresh, authentic Indian cuisine delivered to your door
            </p>
            
            {/* Feature Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30">
                <Truck className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                <h3 className="font-bold text-sm mb-1">Lightning Fast</h3>
                <p className="text-xs opacity-90">25-30 mins</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30">
                <Award className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                <h3 className="font-bold text-sm mb-1">Premium Quality</h3>
                <p className="text-xs opacity-90">Fresh daily</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                <h3 className="font-bold text-sm mb-1">Safe Delivery</h3>
                <p className="text-xs opacity-90">Contactless</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop-Friendly Search and Filter Bar */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <Input
                type="text"
                placeholder="Search for your favorite food..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500 text-lg shadow-lg"
              />
            </div>

            {/* Filter and Sort */}
            <div className="flex space-x-3">
              <button className="flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                <Filter className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Filter</span>
              </button>
              <button className="flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                <TrendingUp className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Sort</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop-Friendly Category Tabs */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex space-x-3 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-2xl font-bold transition-all duration-200 whitespace-nowrap shadow-lg ${
                selectedCategory === 'all' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-xl transform scale-105' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Items
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-2xl font-bold transition-all duration-200 whitespace-nowrap shadow-lg ${
                  selectedCategory === category.id 
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-xl transform scale-105' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop-Friendly Food Items Section */}
      <main 
        style={{ 
          maxWidth: '1280px', 
          margin: '0 auto', 
          padding: '32px 24px 96px 24px',
          width: '100%'
        }}
      >
        <div 
          className="space-y-6"
          style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          {/* Section Header */}
          <div className="text-center">
            <h2 className="text-2xl font-black text-gray-900 mb-2">
              {selectedCategory === 'all' ? '🍽️ All Delicious Items' : `🍽️ ${categories.find(c => c.id === selectedCategory)?.name}`}
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <p className="text-gray-600 font-medium">
                {filteredItems.length} mouth-watering items available
              </p>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            </div>
          </div>
          
          {filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-32 h-32 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Menu className="w-16 h-16 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No items found</h3>
              <p className="text-gray-600 text-lg">Try adjusting your search or category filter</p>
            </div>
          ) : (
            <div 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '24px',
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto'
              }}
            >
              {filteredItems.map((item, index) => (
                <FoodItemCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Floating Cart Button */}
      {getCartItemCount() > 0 && (
        <div className="fixed bottom-8 right-6 z-50">
          <Button
            onClick={() => setIsCartOpen(true)}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-2xl px-8 py-4 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-200 font-bold text-lg"
          >
            <ShoppingCart className="w-6 h-6 mr-3" />
            View Cart ({getCartItemCount()})
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}

      {/* Bottom Spacing */}
      <div className="h-20"></div>
    </div>
  );
};

export default HomePage;